import React from 'react';

// GA4 events. Every event is listed in docs/analytics-events.md.
// gtag is defined by the inline snippet in index.html; it can be missing in prerender,
// in tests, or when a blocker removes it, so every call is guarded.

type Params = Record<string, string | number | boolean | undefined>;

type Gtag = (command: 'event', name: string, params?: Params) => void;

export function track(event: string, params: Params = {}): void {
  if (typeof window === 'undefined') return;
  const gtag = (window as unknown as { gtag?: Gtag }).gtag;
  if (typeof gtag !== 'function') return;
  try {
    gtag('event', event, { page_path: window.location.pathname, ...params });
  } catch {
    // Analytics must never break the page.
  }
}

/** Fire an event once each time `active` turns true (a quiz result or a readout appearing). */
export function useTrackWhen(active: boolean, event: string, params: Params): void {
  const sent = React.useRef(false);
  const key = JSON.stringify(params);
  React.useEffect(() => {
    if (!active) {
      sent.current = false;
      return;
    }
    if (sent.current) return;
    sent.current = true;
    track(event, JSON.parse(key) as Params);
  }, [active, event, key]);
}

const BOOKING_HOST = /(^|\.)cal\.com$/i;

/** Where on the page a link sits: an explicit data-track-location, else a guess from its landmark. */
export function linkLocation(link: Element): string {
  const tagged = link.closest<HTMLElement>('[data-track-location]');
  if (tagged?.dataset.trackLocation) return tagged.dataset.trackLocation;
  if (link.closest('section[aria-label="About the author"]')) return 'author_box';
  if (link.closest('nav')) return 'nav';
  if (link.closest('footer')) return 'footer';
  if (link.closest('header')) return 'hero';
  if (link.closest('aside')) return 'aside';
  return 'body';
}

function onDocumentClick(event: MouseEvent) {
  const target = event.target as Element | null;
  const link = target?.closest?.('a[href]') as HTMLAnchorElement | null;
  if (!link) return;
  const href = link.getAttribute('href') || '';
  if (href.toLowerCase().startsWith('mailto:')) {
    track('email_click', { location: linkLocation(link) });
    return;
  }
  let url: URL;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return;
  }
  if (BOOKING_HOST.test(url.hostname)) {
    track('book_call_click', { location: linkLocation(link), link_url: url.origin + url.pathname });
  }
}

let installed = false;

/** One delegated listener catches every booking and mailto link on the site. Call once, in the browser. */
export function installLinkTracking(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  document.addEventListener('click', onDocumentClick, { capture: true });
}
